<script setup lang="ts">
import { NPopover } from "naive-ui";
import { computed } from "vue";
import { OpenItemChangelog } from "../../models/contador";
import { formatJSDate, toHHMM } from "../../utils/dates";
import { useTeam } from "../../hooks/useTeam";
import { useOrganization } from "../../hooks/useOrganization";
import { getUserEmail } from "../../config/firebase.ts";

const props = defineProps<{
    change: OpenItemChangelog;
    propertyOptions: Array<{ label: string; value: string }>;
}>();

const { employeeMailToName } = useTeam();
const { contactEmailToName, userNameFromContacts } = useOrganization();

const isCreationEvent = computed(() => props.change.to === "created");

const isCurrentUserAuthor = computed(() => {
    return props.change.author === getUserEmail();
});

const displayName = computed(() => {
    if (isCurrentUserAuthor.value && userNameFromContacts.value) {
        return userNameFromContacts.value;
    }
    const employeeName = employeeMailToName(props.change.author);
    if (employeeName !== props.change.author) {
        return employeeName;
    }
    return contactEmailToName(props.change.author);
});

const formatValue = (value: string, field: string) => {
    if ((field.toLowerCase() === "date" || field.toLowerCase() === "due_date") && value) {
        try {
            const date = new Date(value);
            return formatJSDate(date).STANDARD_DATE_FORMAT;
        } catch {
            return value;
        }
    } else if (field.toLowerCase() === "property_id") {
        const property = props.propertyOptions.find((p) => p.value === value);
        return property ? property.label : "Unknown Property";
    } else if (field.toLowerCase() === "amount") {
        return `$${value}`;
    } else {
        return value;
    }
};
</script>

<template>
    <div
        class="px-6 py-2 bg-blue-100 rounded-3xl text-sm flex justify-between gap-4 font-semibold cursor-default"
    >
        <div>
            <span v-if="isCreationEvent">
                Open item created by
                <n-popover trigger="hover">
                    <template #trigger>
                        {{ displayName }}
                    </template>
                    {{ change.author }}
                </n-popover>
            </span>
            <span v-else>
                <n-popover trigger="hover">
                    <template #trigger>
                        {{ displayName }}
                    </template>
                    {{ change.author }}
                </n-popover>
                changed
                {{ change.field.split("_").join(" ") }} from
                <span class="capitalize bg-gray-200 px-2 rounded-lg">{{
                    formatValue(change.from, change.field)
                }}</span>
                <span class="mx-1">→</span>
                <span class="capitalize bg-gray-200 px-2 rounded-lg">{{
                    formatValue(change.to, change.field)
                }}</span>
            </span>
        </div>
        <div class="flex items-center">
            <n-popover trigger="hover">
                <template #trigger>
                    <span class="text-label whitespace-nowrap">
                        {{ toHHMM(new Date(change.createdAt)) }}
                    </span>
                </template>
                {{
                    formatJSDate(new Date(change.createdAt), {
                        include_year: true,
                        include_time: true,
                    }).RELATIVE_DATE_FORMAT
                }}
            </n-popover>
        </div>
    </div>
</template>
